import {
  // ConversionDirection,
  Transaction,
} from 'components/TransactionAllocator';
import {
  calulateRealisedGainsAndFxPerInvestment,
  HoldingClassInvestmentConsiderationValues,
} from './helpers/calculateEquityAndFxGains';
import { compact, sumBy } from 'lodash';
import { ActionTransactionSubTypes } from 'further-types/lp/action';
import { calculateTcyValue } from 'further-ui/utils';
import { CurrencyRateDetails } from 'further-types/lp/transaction';
import { CurrencyCode } from 'further-types/lp/vehicle';
import { ConsiderationTypeHoldingClassTypes } from 'further-types/lp/realisation';

type TransactionWithoutAmounts = Omit<
  Transaction,
  'amountExVatLcy' | 'amountIncVatLcy' | 'amountExVatTcy' | 'amountIncVatTcy'
>;

type BuildRealisedGainsAndFxTransactionsInput = {
  defaultActionTransactions: Array<TransactionWithoutAmounts>;
  holdingClassInvestments: Array<HoldingClassInvestmentConsiderationValues>;
  realisationCurrencyRate: CurrencyRateDetails | null;
  threeCyRealisationCurrencyRate: CurrencyRateDetails | null;
  realisationType: ConsiderationTypeHoldingClassTypes;
  realisationCurrencyCode: CurrencyCode;
};

const buildRealisedGainsAndFxTransactions = ({
  defaultActionTransactions,
  holdingClassInvestments,
  realisationCurrencyRate,
  realisationType,
  threeCyRealisationCurrencyRate,
  realisationCurrencyCode,
}: BuildRealisedGainsAndFxTransactionsInput) => {
  if (holdingClassInvestments.length === 0) {
    return [];
  }

  const realisedGainsAndFxTransactionsPerInvestment =
    calulateRealisedGainsAndFxPerInvestment(
      holdingClassInvestments,
      realisationCurrencyRate,
      threeCyRealisationCurrencyRate,
      realisationType,
    );

  const isThreeCyConsideration =
    realisationType ===
    ConsiderationTypeHoldingClassTypes.ThreeCYNonLcyConsiderationNonLcyHoldingClass;

  const holdingClassCurrency = holdingClassInvestments[0].currency;

  const fxPnlLcy = sumBy(
    realisedGainsAndFxTransactionsPerInvestment,
    'fxPnlLcy',
  );

  const equityPnlTotalLcy = sumBy(
    realisedGainsAndFxTransactionsPerInvestment,
    'equityPnlLcy',
  );

  const fxTransactionType =
    fxPnlLcy >= 0
      ? ActionTransactionSubTypes.RealisedGainFX
      : ActionTransactionSubTypes.RealisedLossFX;

  const equityPnlTransactionType =
    equityPnlTotalLcy >= 0
      ? ActionTransactionSubTypes.RealisedGain
      : ActionTransactionSubTypes.RealisedLoss;

  const defaultFxPnlTransaction = defaultActionTransactions.find(
    (transaction) => transaction.transactionSubType === fxTransactionType,
  );

  const defaultEquityPnlTransaction = defaultActionTransactions.find(
    (transaction) =>
      transaction.transactionSubType === equityPnlTransactionType,
  );

  const transactionCurrencyRate = isThreeCyConsideration
    ? threeCyRealisationCurrencyRate
    : realisationCurrencyRate;

  const fxPnlTransaction = defaultFxPnlTransaction
    ? {
        ...defaultFxPnlTransaction,
        transactionTypeId: defaultFxPnlTransaction?.transactionTypeId,
        amountExVatLcy: Math.abs(fxPnlLcy),
        amountIncVatLcy: Math.abs(fxPnlLcy),
        //this is 0 as per the brief
        amountExVatTcy: 0,
        amountIncVatTcy: 0,
        preCalculatedAmounts: true,
        transactionCurrency: holdingClassCurrency,

        transactionCurrencyRate: transactionCurrencyRate ?? {
          fetchedRate: 1,
          overrideRate: 1,
        },
      }
    : null;

  const equityPnlTransaction = defaultEquityPnlTransaction
    ? {
        ...defaultEquityPnlTransaction,
        amountExVatLcy: Math.abs(equityPnlTotalLcy),
        amountIncVatLcy: Math.abs(equityPnlTotalLcy),
        transactionCurrency:
          realisationType ===
            ConsiderationTypeHoldingClassTypes.ThreeCYNonLcyConsiderationNonLcyHoldingClass ||
          realisationType ===
            ConsiderationTypeHoldingClassTypes.LcyConsiderationForNonLcyHoldingClass
            ? holdingClassCurrency
            : realisationCurrencyCode,
        amountExVatTcy: Math.abs(
          isThreeCyConsideration
            ? calculateTcyValue(
                equityPnlTotalLcy,
                threeCyRealisationCurrencyRate,
              )
            : calculateTcyValue(equityPnlTotalLcy, realisationCurrencyRate),
        ),
        amountIncVatTcy: Math.abs(
          isThreeCyConsideration
            ? calculateTcyValue(
                equityPnlTotalLcy,
                threeCyRealisationCurrencyRate,
              )
            : calculateTcyValue(equityPnlTotalLcy, realisationCurrencyRate),
        ),
        preCalculatedAmounts: isThreeCyConsideration,
        transactionCurrencyRate: transactionCurrencyRate ?? {
          fetchedRate: 1,
          overrideRate: 1,
        },
      }
    : null;

  return compact([fxPnlTransaction, equityPnlTransaction]);
};

export default buildRealisedGainsAndFxTransactions;
